import React from "react"
import { Link, graphql, useStaticQuery } from "gatsby"
import { Helmet } from "react-helmet"
import Layout from "./Layout"
import SEO from "./SEO"
import { DISCOVERY_URL, FULL_PROFILE_URL } from "../constants/screenPrompt"
import { buildProfileText, buildScreenPrompt } from "../lib/buildScreenPrompt"

const query = graphql`
  {
    profileData {
      name
      title
      bio
      availability {
        status
        rate
        currency
        remote_preference
        notice_period
        timezone
        open_to
      }
      skills {
        name
        category
        proficiency
        years
      }
    }
    allJob {
      nodes {
        jobId
        company
        role
        date_start
        date_end
        tech_stack
      }
    }
    allProject(filter: { featured: { eq: true } }) {
      nodes {
        projectId
        title
        description
        tech_stack
      }
    }
    privateProjects: allProject(filter: { featured: { eq: false } }) {
      nodes {
        projectId
        slug
        title
        description
        tech_stack
        tags
      }
    }
    allCert {
      nodes {
        name
        provider
        status
      }
    }
  }
`

// One-tap sample roles so a recruiter, founder, or friend can try a fit in a
// single click — no JD in hand required. Each fills the JD box with a tight,
// realistic role spec that flows through the same self-contained screen prompt.
const ROLE_CHIPS = [
  {
    label: "Agentic AI Engineer",
    jd: "Build production AI agents that take real actions — tool use, multi-step planning, evals, and guardrails. Ship LLM-powered systems (RAG, orchestration, MCP) that are reliable, observable, and safe in production.",
  },
  {
    label: "AI Platform Lead",
    jd: "Own the AI platform: the infrastructure, tooling, and standards that let teams ship LLM features safely. Model orchestration, evaluation pipelines, vector search, cost/latency control, and agent frameworks.",
  },
  {
    label: "Fractional CTO",
    jd: "Own technical architecture for an early-stage SaaS. Stand up CI/CD and quality gates, define engineering standards for both human and AI contributors, and ship fast without breaking production. Part-time, high-leverage.",
  },
  {
    label: "Founding Engineer (0→1)",
    jd: "First engineer on a greenfield product. Take it from zero to a deployed, working system — architecture, backend, infra, and the first AI features — making pragmatic choices that still hold up as it scales.",
  },
  {
    label: "Greenfield Architect",
    jd: "Design a new system from scratch: cloud-native architecture, APIs, data model, CI/CD, and observability. Establish clear patterns that both humans and AI coding tools can build within reliably.",
  },
  {
    label: "Solutions Architect",
    jd: "Design and deliver cloud + AI solutions for clients — architecture, AWS infrastructure, integrations, and delivery. Translate business needs into systems that are scalable, secure, and shippable.",
  },
  {
    label: "DevOps / Platform Eng",
    jd: "Build and run the delivery platform: CI/CD pipelines, Kubernetes, infrastructure-as-code, monitoring, and deployment automation for high-frequency, safe shipping.",
  },
]

const HirePage = () => {
  const data = useStaticQuery(query)
  const p = data.profileData
  const jobs = data.allJob.nodes
  const projects = data.allProject.nodes
  const certs = data.allCert.nodes
  // Private research builds (not featured, tagged "research"): fed to the screening
  // prompt for depth, but deliberately NOT rendered in the visible featured grid.
  const privateProjects = (data.privateProjects.nodes || [])
    .filter((pr) => (pr.tags || []).includes("research"))
    .map((pr) => ({
      title: pr.title,
      description: (pr.description || "").replace(/^A private research build\.\s*/i, ""),
      tech_stack: pr.tech_stack,
      url: `https://nishanttiwari.com/projects/${pr.slug}/`,
    }))
  const [copied, setCopied] = React.useState(false)
  const [jd, setJd] = React.useState("")
  const [prefilled, setPrefilled] = React.useState(false)
  const [aiOpened, setAiOpened] = React.useState(false)

  // Pre-fill the role from a ?jd= link, so the candidate can hand a client a link with
  // the JD already baked in — the client just clicks Copy and pastes into their AI.
  React.useEffect(() => {
    if (typeof window === "undefined") return
    const role = new URLSearchParams(window.location.search).get("jd")
    if (role) {
      setJd(role)
      setPrefilled(true)
    }
  }, [])

  const copyPrompt = async () => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(selfContainedPrompt)
    } else {
      const ta = document.createElement("textarea")
      ta.value = selfContainedPrompt
      ta.style.position = "fixed"
      ta.style.opacity = "0"
      document.body.appendChild(ta)
      ta.select()
      document.execCommand("copy")
      document.body.removeChild(ta)
    }
  }

  const copy = async () => {
    try {
      await copyPrompt()
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch (e) {
      setCopied(false)
    }
  }

  // Open the chosen AI in a new tab with the full prompt already on the clipboard,
  // so the visitor just presses ⌘V / Ctrl+V. We can't auto-type into another origin,
  // and the ~21KB prompt is far too large to pass safely in a ?q= URL (it would
  // truncate), so copy-then-open is the reliable maximum.
  const openInAI = (url) => {
    try {
      copyPrompt() // fire-and-forget; don't await so window.open stays in the gesture
    } catch (e) {}
    setAiOpened(true)
    setTimeout(() => setAiOpened(false), 5000)
    if (typeof window !== "undefined") window.open(url, "_blank", "noopener,noreferrer")
  }

  // group skills by category
  const byCategory = {}
  ;(p.skills || []).forEach((s) => {
    ;(byCategory[s.category] = byCategory[s.category] || []).push(s)
  })

  // Build a SELF-CONTAINED prompt with the full profile embedded inline, so it works
  // in ANY AI — browsing, search-based, or offline — with zero retrieval (no fetching,
  // no search-index lag, no bot-blocking). The same builder powers scripts/make-hire-link.js
  // so the page and the generated /s/<slug>.txt files can never drift.
  const profileText = buildProfileText({
    name: p.name,
    title: p.title,
    bio: p.bio,
    availability: p.availability,
    skills: p.skills,
    jobs,
    projects,
    privateProjects,
    certs,
  })

  const selfContainedPrompt = buildScreenPrompt(profileText, jd)

  const a = p.availability || {}
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Person",
    name: p.name,
    jobTitle: p.title,
    description: p.bio,
    url: "https://nishanttiwari.com/hire",
    knowsAbout: (p.skills || []).map((s) => s.name),
  }

  return (
    <Layout>
      <SEO
        title="Screen me objectively | Nishant Tiwari"
        description="Don't take a résumé's word for it. Paste one prompt into your AI and have it screen Nishant Tiwari against your role — objectively, from a full machine-readable profile on this page."
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>
      <section className="hire-page">
        <div className="section-center hire-center">
          <p className="hire-kicker">For recruiters &amp; hiring AI</p>
          <h1 className="hire-title">
            Screen me — <span>objectively.</span>
          </h1>
          <p className="hire-lead">
            Don't take a résumé's word for it. Pick a role below or paste your own, copy
            the prompt, and drop it into ChatGPT, Claude, or Gemini — your AI reads the full
            profile on this page and scores the fit. No sign-up, no install.
          </p>

          <div className="hire-prompt-card">
            <div className="hire-prompt-head">
              <span>
                {prefilled
                  ? "✓ Pre-loaded for your role — just Copy & paste into your AI"
                  : "1. Pick or paste a role  2. Copy  3. Paste into your AI"}
              </span>
              <button type="button" className="btn hire-copy-btn" onClick={copy}>
                {copied ? "Copied ✓ — paste into your AI" : "Copy prompt + profile"}
              </button>
            </div>
            <div className="hire-chips">
              <span className="hire-chiplabel">Try a role:</span>
              {ROLE_CHIPS.map((c) => (
                <button
                  type="button"
                  key={c.label}
                  className={`hire-chip${jd.trim() === c.jd ? " active" : ""}`}
                  onClick={() => {
                    setJd(c.jd)
                    setPrefilled(false)
                    setCopied(false)
                  }}
                >
                  {c.label}
                </button>
              ))}
            </div>
            <textarea
              className="hire-jd"
              value={jd}
              onChange={(e) => setJd(e.target.value)}
              rows={5}
              placeholder="Pick a role above, or paste a job description here — it gets baked into the copied prompt. (Optional: leave blank and add the role in your AI.)"
            />
            <p className="hire-note">
              <strong>Self-contained:</strong> Copy bundles the full profile (shown below)
              {jd.trim() ? " plus the role above" : ""} into one prompt — paste it into{" "}
              <em>any</em> AI, even one that can't browse the web. No fetching, no sign-up,
              no guessing.
            </p>
            <div className="hire-openrow">
              <span>Copy &amp; open in:</span>
              <button type="button" className="hire-openlink" onClick={() => openInAI("https://chatgpt.com/")}>
                ChatGPT ↗
              </button>
              <button type="button" className="hire-openlink" onClick={() => openInAI("https://claude.ai/new")}>
                Claude ↗
              </button>
              <button type="button" className="hire-openlink" onClick={() => openInAI("https://gemini.google.com/app")}>
                Gemini ↗
              </button>
              {aiOpened && (
                <span className="hire-openhint">✓ Prompt copied — press ⌘V / Ctrl+V to paste</span>
              )}
            </div>
          </div>

          {/* Full machine- AND human-readable profile — so any AI that reaches this
              page (fetch or search) can screen without a separate JSON request. */}
          <div className="hire-profile">
            <h2 className="hire-h2">The full profile</h2>
            <p className="hire-bio">{p.bio}</p>

            <p className="hire-avail">
              <strong>Availability:</strong> {a.status} · ${a.rate}/{a.currency || "USD"}
              {" "}per hour · {a.remote_preference === "remote_only" ? "remote only" : a.remote_preference}
              {" "}· notice: {a.notice_period} · {a.timezone}
              {a.open_to && a.open_to.length ? ` · open to ${a.open_to.join(", ")}` : ""}
            </p>

            <h3 className="hire-h3">Skills</h3>
            {Object.keys(byCategory).map((cat) => (
              <p className="hire-skillrow" key={cat}>
                <span className="hire-cat">{cat}:</span>{" "}
                {byCategory[cat]
                  .map((s) => `${s.name} (${s.proficiency}/5, ${s.years}y)`)
                  .join(", ")}
              </p>
            ))}

            <h3 className="hire-h3">Experience</h3>
            <ul className="hire-list">
              {jobs.map((j) => (
                <li key={j.jobId}>
                  <strong>{j.role}</strong>, {j.company}{" "}
                  <span className="hire-dim">
                    ({j.date_start}–{j.date_end || "present"})
                  </span>
                  {j.tech_stack && j.tech_stack.length ? (
                    <span className="hire-dim"> · {j.tech_stack.join(", ")}</span>
                  ) : null}
                </li>
              ))}
            </ul>

            <h3 className="hire-h3">Featured projects</h3>
            <ul className="hire-list">
              {projects.map((pr) => (
                <li key={pr.projectId}>
                  <strong>{pr.title}</strong> — {pr.description}
                  {pr.tech_stack && pr.tech_stack.length ? (
                    <span className="hire-dim"> ({pr.tech_stack.join(", ")})</span>
                  ) : null}
                </li>
              ))}
            </ul>

            <h3 className="hire-h3">Certifications</h3>
            <p className="hire-skillrow">
              {certs.map((c) => `${c.name}${c.status ? ` [${c.status}]` : ""}`).join(" · ")}
            </p>
          </div>

          <p className="hire-foot">
            Machine-readable for agents: <a href={DISCOVERY_URL}>discovery JSON</a> ·{" "}
            <a href={FULL_PROFILE_URL}>full JSON</a> ·{" "}
            <Link to="/for-ai">API &amp; MCP server</Link>
          </p>
        </div>
      </section>
    </Layout>
  )
}

export default HirePage
